import type { StrategyNode } from '../types/BaseNode';
import type { TunableParam } from '../types/TunableParam';
import { SchemaService } from '../services/SchemaService';

/**
 * Recursively extracts tunable parameters from a strategy tree.
 * Fetches PropertySpec metadata to determine correct control types.
 */
export async function extractTunableParams(
    node: StrategyNode | null | undefined,
    path: string = 'Root'
): Promise<TunableParam[]> {
    if (!node) return [];

    const params: TunableParam[] = [];
    const currentPath = node.title ? `${path} > ${node.title}` : path;

    // Extract from config.props (for Single and Formatter nodes)
    if ('config' in node && node.config && 'props' in node.config) {
        const props = node.config.props as Record<string, any>;
        const typeId = node.config.typeId as string;
        const nodeType = node.type as 'Single' | 'Formatter';

        // Extract tunable parameters
        for (const [key, value] of Object.entries(props)) {
            // Use SchemaService for lookup (allows for async/lazy loading if we need it in future actions)
            // Note: extractTunableParams is async, so we can await if needed, but for now
            // SchemaService has helper methods. However, we need to be careful about 
            // the fact that this is a hot path.

            // To avoid N+1 async calls, we might want to fetch the type spec once per node type.
            // But for now, let's keep it simple and safe.
            const spec = await SchemaService.getPropertySpec(nodeType, typeId, key);

            // Only include parameters that make sense to tune
            // Exclude: dates, text fields (unless they're meaningful numbers stored as strings)
            const isTunable =
                spec?.control === 'number' ||
                spec?.control === 'dropdown' ||
                spec?.control === 'checkbox';

            if (isTunable && spec) {
                params.push({
                    nodeId: node.id,
                    nodePath: currentPath,
                    // Use node.title if available, otherwise use typeId (e.g., "classify", "linepack")
                    nodeTitle: node.title || typeId,
                    nodeType: nodeType,
                    typeId,
                    paramKey: key,
                    paramValue: value,
                    spec,
                });
            }
        }
    }

    // Recurse into child nodes
    if (node.type === 'Formatter' && 'config' in node && node.config.child) {
        const childParams = await extractTunableParams(node.config.child, currentPath);
        params.push(...childParams);
    } else if (node.type === 'Composite' && 'config' in node) {
        if (node.config.left) {
            const leftParams = await extractTunableParams(node.config.left, currentPath);
            params.push(...leftParams);
        }
        if (node.config.right) {
            const rightParams = await extractTunableParams(node.config.right, currentPath);
            params.push(...rightParams);
        }
    }

    return params;
}
